/**
 * Raised when a composition reference could NOT be looked up for a reason that is not "the
 * reference does not exist" — a 5xx, a network failure, a timeout.
 *
 * The distinction matters because `verifyArtifact` has exactly one way to report a reference it
 * cannot see: `{ status: 'unresolved' }` → `REF_UNRESOLVED`, an *error* finding that means "this
 * artifact names a composition that isn't there". Feeding a transport failure into that channel
 * makes a perfectly valid document show a red check panel, and makes composer-agent-service reject
 * the agent's proposal with a validator reason that was never true. A 404 IS genuine absence (the
 * read routes return an identical 404 for "no such composition" and "not visible to you", by
 * design — both mean the artifact cannot legally reference it), so 404 stays `unresolved`.
 * Everything else aborts the warm-up and surfaces as "the check could not run", never as a finding.
 */
export class CompositionResolutionUnavailableError extends Error {
  readonly systemName: string;
  readonly version?: number;
  readonly status?: number;

  constructor(systemName: string, options: { version?: number; status?: number; cause?: unknown } = {}) {
    const statusText = options.status === undefined ? '' : ` (HTTP ${options.status})`;
    const versionText = options.version === undefined ? '' : `@${options.version}`;
    super(`Could not load composition reference ${systemName}${versionText}${statusText}`);
    this.name = 'CompositionResolutionUnavailableError';
    this.systemName = systemName;
    this.version = options.version;
    this.status = options.status;
    if (options.cause !== undefined) (this as { cause?: unknown }).cause = options.cause;
  }
}

/** `true` when `err` reports a reference that genuinely is not there (404) rather than a lookup that failed. */
export function isNotFoundStatus(status: number | undefined): boolean {
  return status === 404;
}

/** Best-effort HTTP status off an axios-shaped or fetch-shaped rejection; `undefined` when the failure carried none (network error, timeout). */
export function httpStatusOf(err: unknown): number | undefined {
  const anyErr = err as { status?: unknown; response?: { status?: unknown } } | null | undefined;
  const status = anyErr?.response?.status ?? anyErr?.status;
  return typeof status === 'number' ? status : undefined;
}
